import React from 'react';

const GradientPreview = ({ config }) => {
  const {
    colors = ['#F26F29', '#F03224'],
    mode = 'linear',
    direction = 'vertical',
    customAngle = 0,
    angle = 0,
    useStops = false,
    stops = [],
    radialCenter = 'center',
    radialCenterX = 0.5,
    radialCenterY = 0.5,
    endRadius = 100,
    angularCenter = 'center',
    angularCenterX = 0.5,
    angularCenterY = 0.5,
    startAngle = 0,
    endAngle = 360,
    gradientOpacity = 1.0,
    componentType = 'view',
    hasCornerRadius = false,
    cornerRadius = 0,
    shape = 'rectangle',
    buttonTitle = 'Button',
    textColor = '#FFFFFF',
    fontSize = 16,
    fontWeight = 'regular',
    sizingMode = 'fixed',
    width = 200,
    height = 50,
    autolayoutType = 'fullWidth',
    padding = 16,
    aspectRatio = 1.0,
  } = config;

  // Helper to convert center preset to CSS percentage
  const getCenterPosition = (center, centerX, centerY) => {
    if (center === 'custom') {
      return `${centerX * 100}% ${centerY * 100}%`;
    }
    const presetMap = {
      'center': '50% 50%',
      'top': '50% 0%',
      'bottom': '50% 100%',
      'leading': '0% 50%',
      'trailing': '100% 50%',
      'topLeading': '0% 0%',
      'topTrailing': '100% 0%',
      'bottomLeading': '0% 100%',
      'bottomTrailing': '100% 100%',
    };
    return presetMap[center] || '50% 50%';
  };

  // Helper to get gradient colors/stops string
  const getGradientColors = () => {
    if (useStops && stops && stops.length > 0) {
      return stops.map(s => `${s.color} ${s.location * 100}%`).join(', ');
    }
    return colors.join(', ');
  };

  const getGradientStyle = () => {
    const baseStyle = {
      opacity: gradientOpacity,
    };

    if (mode === 'linear') {
      let gradientDirection = '';
      if (direction === 'custom') {
        // Convert angle to CSS gradient angle
        // CSS uses 0deg = to top, 90deg = to right
        const cssAngle = customAngle;
        gradientDirection = `${cssAngle}deg`;
      } else {
        switch (direction) {
          case 'vertical':
            gradientDirection = 'to bottom';
            break;
          case 'horizontal':
            gradientDirection = 'to right';
            break;
          case 'diagonalTL':
            gradientDirection = 'to bottom right';
            break;
          case 'diagonalTR':
            gradientDirection = 'to bottom left';
            break;
          case 'diagonalBL':
            gradientDirection = 'to top right';
            break;
          case 'diagonalBR':
            gradientDirection = 'to top left';
            break;
          case 'topToBottom':
            gradientDirection = 'to bottom';
            break;
          case 'leftToRight':
            gradientDirection = 'to right';
            break;
          default:
            gradientDirection = 'to bottom';
        }
      }
      baseStyle.background = `linear-gradient(${gradientDirection}, ${getGradientColors()})`;
    } else if (mode === 'radial') {
      const centerPos = getCenterPosition(radialCenter, radialCenterX, radialCenterY);
      baseStyle.background = `radial-gradient(circle ${endRadius}px at ${centerPos}, ${getGradientColors()})`;
    } else if (mode === 'elliptical') {
      const centerPos = getCenterPosition(radialCenter, radialCenterX, radialCenterY);
      baseStyle.background = `radial-gradient(ellipse ${endRadius}px at ${centerPos}, ${getGradientColors()})`;
    } else if (mode === 'angular') {
      const centerPos = getCenterPosition(angularCenter, angularCenterX, angularCenterY);
      if (startAngle !== 0 || endAngle !== 360) {
        // Partial arc - approximate with conic gradient
        baseStyle.background = `conic-gradient(from ${startAngle}deg at ${centerPos}, ${getGradientColors()})`;
      } else {
        baseStyle.background = `conic-gradient(from ${angle}deg at ${centerPos}, ${getGradientColors()})`;
      }
    }
    return baseStyle;
  };

  const getShapeStyles = () => {
    const baseStyle = {
      ...getGradientStyle(),
    };

    // Apply corner radius if enabled
    if (hasCornerRadius) {
      baseStyle.borderRadius = `${cornerRadius}px`;
    }

    // Apply shape-specific styles
    switch (shape) {
      case 'circle':
        baseStyle.borderRadius = '50%';
        baseStyle.aspectRatio = '1 / 1';
        break;
      case 'square':
        baseStyle.aspectRatio = '1 / 1';
        break;
      case 'capsule':
        baseStyle.borderRadius = '9999px';
        break;
      case 'roundedRectangle':
        if (!hasCornerRadius) {
          baseStyle.borderRadius = '8px';
        }
        break;
      default:
        // rectangle - no special styling
        break;
    }

    return baseStyle;
  };

  const getSizingStyles = () => {
    const sizingStyle = {};

    if (sizingMode === 'fixed') {
      sizingStyle.width = `${width}px`;
      sizingStyle.height = `${height}px`;
    } else {
      // AutoLayout mode
      switch (autolayoutType) {
        case 'fullWidth':
          sizingStyle.width = '100%';
          sizingStyle.maxWidth = '100%';
          break;
        case 'fullHeight':
          sizingStyle.height = '100%';
          sizingStyle.maxHeight = '100%';
          break;
        case 'padding':
          sizingStyle.width = `calc(100% - ${padding * 2}px)`;
          sizingStyle.margin = `0 ${padding}px`;
          break;
        case 'aspectRatio':
          sizingStyle.aspectRatio = `${aspectRatio} / 1`;
          sizingStyle.width = '100%';
          break;
        default:
          break;
      }
    }

    return sizingStyle;
  };

  const getFontWeight = () => {
    switch (fontWeight) {
      case 'medium':
        return '500';
      case 'semibold':
        return '600';
      case 'bold':
        return '700';
      default:
        return '400';
    }
  };

  const shapeStyles = getShapeStyles();
  const sizingStyles = getSizingStyles();
  const combinedStyle = { ...shapeStyles, ...sizingStyles };

  const renderContent = () => {
    if (componentType === 'button') {
      return (
        <button
          style={combinedStyle}
          className="shadow-2xl flex items-center justify-center cursor-pointer"
          onClick={(e) => e.preventDefault()}
        >
          <span
            style={{
              color: textColor,
              fontSize: `${fontSize}px`,
              fontWeight: getFontWeight(),
            }}
          >
            {buttonTitle}
          </span>
        </button>
      );
    } else {
      return (
        <div
          style={combinedStyle}
          className="shadow-2xl flex items-center justify-center"
        >
          {componentType === 'button' && buttonTitle && (
            <span
              style={{
                color: textColor,
                fontSize: `${fontSize}px`,
                fontWeight: getFontWeight(),
              }}
            >
              {buttonTitle}
            </span>
          )}
        </div>
      );
    }
  };

  return (
    <div
      className="w-full h-full flex items-center justify-center"
      style={{ minWidth: 0, minHeight: 0, padding: '16px', boxSizing: 'border-box' }}
    >
      {renderContent()}
    </div>
  );
};

export default GradientPreview;

